<template>
  <div
    class="atom-input-images"
    :class="{
      'is-dragging': isDragging,
    }"
    @drop.prevent="onDrop"
    @dragover.prevent="onDragOver"
    @dragleave.prevent="onDragLeave"
  >
    <label
      :for="name"
      class="atom-input-images__label"
    >{{ label }}</label>

    <input
      :id="name"
      ref="inputRef"
      :name="name"
      type="file"
      :multiple="maxAmount > 1 || maxAmount === 0"
      :accept="accept || null"
      class="atom-input-images__input"
      @change="onFileChange"
    />

    <div
      v-if="errors.includes(true)"
      class="atom-input-images__error"
    >
      {{ errorText }}
    </div>

    <div class="atom-input-images__images">
        <div 
            v-if="queuedFiles.length === 0"
            class="atom-input-images__icon-wrapper"
        >
            <IonIcon
                icon-name="receipt-upload"
                icon-size="54px"
                class="atom-input-images__icon"
            />
        </div>

      <div
        v-else
        class="atom-input-images__images-container"
      >
        <div
          v-for="(image, index) in queuedFiles"
          :key="`file-${componentUid}-${image.name}`"
          class="atom-input-images__image"
          :class="{ 'has-image-error': errors[index] }"
          @click="onRemove(index)"
          @keypress="onRemove(index)"
        >
          <Transition name="fade-in">
            <div 
            v-if="imagesLoaded[index] || isPdfFile(image.file)" 
                class="atom-input-images__image-close-wrapper"
            >
                <IonIcon
                   icon-name="close"
                   icon-color="var(--c-primary)"
                   icon-size="6px"
                />
            </div>
          </Transition>

          <div
            v-if="isPdfFile(image.file)"
            class="atom-input-images__file"
          >
            <div class="atom-input-images__file-icon">
                <IonIcon icon-name="pdf-file" icon-size="51px" />
            </div>

            <div class="atom-input-images__file-name">
                {{ image.name }}
            </div>
          </div>

          <AtomImage
            v-else
            direction="height"
            class="atom-input-images__image"
            :image="{
              src: createObjectURL(image.file),
              alt: image.name,
            }"
            @on-load="onImageLoad(index)"
          />
        </div>
      </div>
    </div>

    <div class="atom-input-images__controls">
      <div class="atom-input-images__info" v-html="dropzoneText"></div>

      <AtomButton
      v-if="!hasQueuedFiles"
        :text="buttonText"
        :background-color="hasQueuedFiles ? 'var(--c-primary-100)' : 'var(--c-primary)'"
        :text-color="hasQueuedFiles ? 'var(--c-primary-500)' : 'var(--c-white)'"
        :border-color="hasQueuedFiles ? 'var(--c-primary-100)' : 'var(--c-primary)'"
        :is-disabled="maxReached"
        @click="onClickAdd"
      />

      <AtomButton
        v-if="hasQueuedFiles"
        :text="buttonUploadText"
        :is-disabled="errors.includes(true) || isValidationPending"
        :callback="onUpload"
      />
    </div>

    <AtomLoaderLogo
      :is-loading="isLoading"
      :text="loadingTitle"
      :subtitle="loadingSubtitle"
      position="absolute"
    />
  </div>
</template>

<script setup>
const props = defineProps({
    name: {
        type: String,
        required: true,
    },

    label: {
        type: String,
        default: '',
    },

    buttonText: {
        type: String,
        default: '',
    },

    buttonUploadText: {
        type: String,
        default: '',
    },

    errorText: {
        type: String,
        default: '',
    },

    dropzoneText: {
        type: String,
        default: '',
    },

    imageErrorText: {
        type: String,
        default: '',
    },

    /* Settings */
    validations: {
        type: Array,
        default: () => [],
    },

    maxAmount: {
        type: Number,
        default: 1,
    },

    accept: {
        type: String,
        default: '',
    },

    backgroundColor: {
        type: String,
        default: 'var(--c-secondary--light)',
    },

    isLoading: {
        type: Boolean,
        default: false,
    },

    loadingTitle: {
        type: String,
        default: 'Viel Glück!',
    },

    loadingSubtitle: {
        type: String,
        default: 'Bilder werden hochgeladen...',
    },
});

const emit = defineEmits(['on-submit', 'set-error']);
const componentUid = getUid();

const {
    backgroundColor,
} = toRefs(props);

const queuedFiles = ref([]);
const inputRef = ref(null);
const isDragging = ref(false);
const hasQueuedFiles = computed(() => queuedFiles.value.length > 0);
const maxReached = computed(
    () => props.maxAmount > 1
    && queuedFiles.value.length === props.maxAmount,
);

/*
    Handle input
*/
const { validate } = useImageValidations();
const errors = ref([]);

const handleEmits = () => {
    /* Create a new filelist from queuedFiles array */
    const fileList = queuedFiles.value.map((item) => item.file);

    emit('on-submit', fileList);
    // useStorybookAction('on-submit', fileList);
};

const isValidationPending = ref(false);
const validateFiles = async () => {
    isValidationPending.value = true;
    errors.value = [];
    const mappedFiles = queuedFiles.value.map((item) => item.file);

    // eslint-disable-next-line no-restricted-syntax
    for (const [index, file] of mappedFiles.entries()) {
        errors.value.push(false);
        const fileSpecificErrors = [];

        // eslint-disable-next-line no-await-in-loop
        await Promise.all(props.validations.map(async (validation) => {
            const result = await validate(file, validation);
            if (!result) {
                fileSpecificErrors.push(validation.message);
            }
        }));

        errors.value[index] = fileSpecificErrors.length > 0;
    }

    isValidationPending.value = false;
};
const onFileChange = async (event, reset) => {
    const shouldReplaceQueuedFiles = reset || props.maxAmount === 1;
    if (shouldReplaceQueuedFiles) {
        queuedFiles.value = [];
        imagesLoaded.value = {};
    }

    const filesArray = Array.from(event.target.files);
    const mappedFiles = filesArray.map((file) => ({
        file,
        name: file.name,
    }));

    if (props.maxAmount === 1) {
        queuedFiles.value = mappedFiles.slice(-1);
    } else {
        queuedFiles.value = [...queuedFiles.value, ...mappedFiles];
    }

    // Remove files if max amount is reached
    if (props.maxAmount > 0 && queuedFiles.value.length > props.maxAmount) {
        queuedFiles.value = queuedFiles.value.slice(0, props.maxAmount);
    }

    await validateFiles();
};

/*
    Handle drop
*/
const onDragOver = () => {
    isDragging.value = true;
};

const onDragLeave = () => {
    isDragging.value = false;
};

const onDrop = (event) => {
    isDragging.value = false;
    const { files } = event.dataTransfer;
    if (isFileListNotEmpty(files)) {
        onFileChange({ target: { files } });
    }
};

/*
  Create object URL to display the queued images
*/
const createObjectURL = (file) => URL.createObjectURL(file);
const isPdfFile = (file) => file?.type === 'application/pdf' || file?.name?.toLowerCase().endsWith('.pdf');

/*
  Trigger the input field click over the button
*/
const onClickAdd = () => {
    if (props.maxAmount === 1) {
        inputRef.value.value = null;
    }

    inputRef.value.click();
};

/*
  Remove a file from the queue
*/
const updateInputFileList = () => {
    const dataTransfer = new DataTransfer();
    queuedFiles.value.forEach((fileObj) => {
        dataTransfer.items.add(fileObj.file);
    });

    inputRef.value.files = dataTransfer.files;
};

const imagesLoaded = ref({});
const onRemove = (index) => {
    imagesLoaded.value = {};
    queuedFiles.value.splice(index, 1);

    if (isArrayNotEmpty(queuedFiles.value)) {
        queuedFiles.value.forEach((file, i) => {
            imagesLoaded.value[i] = true;
        });
    }

    updateInputFileList();
    validateFiles();
};

/*
  Listen on load event on image
*/
const onImageLoad = (index) => {
    imagesLoaded.value[index] = true;
};

/*
  Upload
*/
const onUpload = () => {
    handleEmits();
};

const resetValue = () => {
    queuedFiles.value = [];
    errors.value = [];
    imagesLoaded.value = {};
    isDragging.value = false;
    isValidationPending.value = false;
    if (inputRef.value) {
        inputRef.value.value = null; // clear native file input
    }
    emit('on-reset');
};

defineExpose({
    resetValue,
});
</script>

<style lang="scss" scoped>
.atom-input-images {
    @include fluid-simple('padding', 20px, 40px);

    position: relative;
    display: flex;
    flex-direction: column;
    background-color: v-bind(backgroundColor);
    row-gap: 20px;
}

.atom-input-images__images,
.atom-input-images__controls {
    display: flex;
    align-items: center;
    justify-content: center;
}

.atom-input-images__controls {
    flex-direction: column;
    row-gap: 20px;
}

.atom-input-images__images {
    // min-height: 200px;
}

.atom-input-images__images-container {
    @include fluid-simple('column-gap', 14px, 16px);
    @include fluid-simple('row-gap', 14px, 16px);

    display: flex;
    flex-wrap: wrap;
    justify-content: center;
}

.atom-input-images__icon-wrapper {
    display: flex;
    width: 80px;
    height: 80px;
    align-items: center;
    justify-content: center;
    border: 1px solid var(--c-white);
    border-radius: 16px;
    margin: 0 auto;
    background-color: rgba(255, 255, 255, 0.48);
}

@keyframes shake {
    0% {
        transform: translateX(0);
    }
    25% {
        transform: rotate(2deg);
    }
    50% {
        transform: rotate(-2deg);
    }
    75% {
        transform: rotate(2deg);
    }
    100% {
        transform: translateX(0);
    }
}

.atom-input-images__image {
    @include fluid-simple('height', 140px, 204px);

    position: relative;
    overflow: hidden;
    border: 1px solid var(--c-white);
    border-radius: 16px;
    cursor: pointer;

    &.has-image-error {
        animation: shake 0.5s 3;
        animation-delay: 1s;
    }


    &.has-image-error:after {
        background: var(--c-error--transparent);
    }
}

.atom-input-images__file {
    @include fluid-simple('height', 74px, 104px);
    @include fluid-simple('width', 110px, 160px);
    @include fluid-simple('padding', 8px, 12px);

    position: relative;
    z-index: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    row-gap: 6px;
}

.atom-input-images__file-name {
    position: absolute;
    bottom: -20px;
    overflow: hidden;
    max-width: 80%;
    color: var(--c-primary-500);
    font-family: var(--f-family--primary);
    font-size: 12px;
    line-height: 14px;
    text-align: center;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.atom-input-images__info {
    max-width: 300px;
    color: var(--c-primary);
    font-family: var(--f-family--primary);
    font-size: var(--f-size--description);
    font-weight: var(--f-weight--400);
    line-height: var(--l-height-description);
    text-align: center;
}

.atom-input-images__label,
.atom-input-images__input {
    display: none;
}

.atom-input-images__icon {
    transition: transform 0.2s ease-in-out;
    .is-dragging & {
        transform: scale(1.1);
    }
}

.atom-input-images__image-close-wrapper {
    @include z-index('imageUploadIcon');
    @include fluid-simple('width', 16px, 16px);
    @include fluid-simple('height', 16px, 16px);

    position: absolute;
    top: 10px;
    right: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 16px;
    background-color: #E5EDF8;
    cursor: pointer;
}

.atom-input-images__error {
    color: var(--c-error);
    font-family: var(--f-family--primary);
    font-size: var(--f-size--description);
    font-weight: var(--f-weight--400);
    line-height: var(--l-height-description);
    text-align: center;
}

.atom-input-images__file-icon {
    display: flex;
    width: 80px;
    height: 80px;
    align-items: center;
    justify-content: center;
    border: 1px solid var(--c-white);
    border-radius: 16px;
    margin: 0 auto;
    aspect-ratio: 1 / 1;
    background-color: rgba(255, 255, 255, 0.48);
}
</style>
